import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Field } from '../ui/Field'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/**
 * Formularul de transfer catre gestiunea de sampling.
 *
 * A stat pana acum in `SamplingTransferuri.tsx`, langa lista si langa logica de primire. A iesit
 * de acolo fiindca se deschide din doua locuri: fila Transferuri a unui eveniment si ecranul
 * general din Actiuni. Doua copii ale aceluiasi formular ar fi trebuit tinute in pas cu mana, iar
 * prima ramasa in urma ar fi fost cea din Actiuni, la care nu se mai uita nimeni.
 */

interface Preview {
  payload: Record<string, unknown>
  corp: string
  piedici: string[]
}

interface ProdusCatalog {
  id: number
  sku: string
  name: string
  stock: string | null
  /** Stocul din gestiunea sursa, citit din EA. `null` = n-am putut citi. */
  stocEa: number | null
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

export interface PropsModalTransfer {
  onClose: () => void
  /**
   * Retur: marfa se întoarce din gestiunea de sampling în depozit.
   *
   * E același document, cu sursa și destinația schimbate între ele — de aceea nu are ecran
   * propriu. Se schimbă doar ce e presetat și ce scrie pe buton, ca omul să nu completeze
   * invers din greșeală tocmai când e obosit, după eveniment.
   */
  retur?: boolean
  /**
   * ÎNREGISTRARE, nu transfer: mutarea s-a făcut deja în ExpertAccounts, aici doar o notăm.
   *
   * Nu pleacă niciun document. Există fiindcă a reface din CRM o mutare făcută în ERP ar fi o
   * A DOUA mutare reală — marfa ar ieși încă o dată din gestiunea sursă (Alexandru, 29.09.2026,
   * după transferul Adrian → Doru făcut direct în EA).
   */
  inregistrare?: boolean
  /** Activitatea pentru care pleacă marfa. Gol = transfer general, ca în Acțiuni. */
  campanieId?: number
  /** Cererea de necesar din care pornește; liniile ei vin precompletate cu restul netransferat. */
  cerere?: {
    id: number
    /** Cine a cerut marfa. Devine destinația implicită, dacă are gestiune proprie. */
    ceruteDeId?: number
    linii: { id: number; productId: number; denumire: string; rest: number }[]
  }
}

export function ModalTransfer({
  onClose,
  campanieId,
  cerere,
  retur,
  inregistrare,
}: PropsModalTransfer) {
  /** Numărul documentului din EA — dovada mutării. Fără el, înregistrarea se refuză pe server. */
  const [eaDocNo, setEaDocNo] = useState('')
  const [dataMutarii, setDataMutarii] = useState(() => new Date().toISOString().slice(0, 10))
  const queryClient = useQueryClient()
  /*
    La retur, sursa e gestiunea de sampling, iar destinația se alege (de obicei depozitul).

    La transfer pornim din „Mostre" (7), nu din „Depozit" (1): mostrele de 10 capsule stau acolo,
    iar pornind din Depozit EA răspunde „Stoc insuficient" — s-a întâmplat de trei ori într-o
    dimineață, pe 22.09.2026, înaintea unui eveniment.
  */
  const [locSursa, setLocSursa] = useState(retur ? '27' : '7')
  /**
   * Cui i se duce marfa.
   *
   * Victor: „ei au nevoie de un stock al lor pe care să îl poarte în mașină". Fără agent ales,
   * marfa merge în gestiunea comună a modulului — și atunci nimeni nu răspunde de ea.
   */
  /**
   * Destinația, codificată: `""` = gestiunea comună, `agent:<id>`, `loc:<id>`.
   *
   * Alexandru: „la Către am ca opțiune o singură gestiune". Așa era — lista conținea doar
   * agenții cu gestiune proprie, iar niciunul n-avea încă. Un ecran care oferă o singură
   * opțiune nu e o alegere.
   */
  const [catre, setCatre] = useState('')
  /* S-a pus deja destinația implicită? O singură dată: o alegere a omului nu se suprascrie. */
  const implicitPus = useRef(false)
  const [observatii, setObservatii] = useState('')
  /*
    Pornit dintr-o cerere, formularul vine completat cu RESTUL netransferat, nu cu ce s-a cerut:
    a doua transa trebuie sa porneasca de la cat a mai ramas, nu de la zero si nici de la tot.
  */
  const [selectie, setSelectie] = useState<Record<number, number>>(() =>
    Object.fromEntries(
      (cerere?.linii ?? []).filter((l) => l.rest > 0).map((l) => [l.productId, l.rest]),
    ),
  )
  /** Ce linie de cerere stinge fiecare produs — se trimite mai departe pe fiecare linie. */
  const linieCererePeProdus = new Map((cerere?.linii ?? []).map((l) => [l.productId, l.id]))
  const [preview, setPreview] = useState<Preview | null>(null)

  /**
   * Gestiunile din EA, pentru selectorul de sursă.
   *
   * Câmpul cerea până acum un număr — „locid din EA" — pe care omul trebuia să-l știe. Costul
   * s-a văzut pe 21.08.2026: transferul a plecat din gestiunea 1, „Depozit", unde produsul are
   * stoc 0. Mostrele stau în gestiunea 7, care se numește chiar „Mostre".
   */
  const gestiuni = useQuery({
    queryKey: ['ea', 'gestiuni'],
    queryFn: async () =>
      (
        await api.get<{
          gestiuni: { id: number; nume: string; grup: string | null; faraStoc: boolean }[]
        }>('/ea/gestiuni')
      ).data.gestiuni,
    retry: false,
  })

  /*
    Catalogul, cu stocul din gestiunea ALEASĂ. Se reîncarcă la schimbarea sursei: stocul e
    proprietatea gestiunii, nu a produsului.
  */
  const catalog = useQuery({
    queryKey: ['sampling', 'produse', locSursa],
    queryFn: async () =>
      (
        await api.get<{ produse: ProdusCatalog[]; stocCitit?: boolean }>(
          '/marketing/sampling/produse',
          { params: locSursa ? { locId: Number(locSursa) } : {} },
        )
      ).data,
  })

  const agenti = useQuery({
    queryKey: ['sampling', 'agenti-transfer'],
    queryFn: async () =>
      (
        await api.get<{ users: { id: number; name: string; eaLocId: number | null }[] }>(
          '/settings/users/echipa',
        )
      ).data.users.filter((u) => u.eaLocId),
  })

  /*
    DESTINAȚIA IMPLICITĂ: cine a cerut marfa.

    Alexandru, 30.09.2026: „dacă Dorian a pus cererea de necesar, transferul de ce s-a făcut în
    gestiunea Sampling?". Fiindcă nimic nu le lega. Cererea știa cine a cerut; formularul de
    transfer pornea cu „Gestiunea comună de sampling" selectată, indiferent. Cine nu schimba
    lista trimitea marfa în 27, iar documentul ieșea „reușit".

    Nu e o corecție de ecran, e cauza greșelii. Marfa pentru „Her Moment" a ajuns în Sampling în
    loc de gestiunea lui Dorian exact așa.

    Se pune O SINGURĂ DATĂ, și doar peste o alegere nefăcută: cine vrea altă destinație o
    schimbă, și nu i se schimbă înapoi sub mână.
  */
  useEffect(() => {
    if (implicitPus.current || catre !== '' || retur || !cerere?.ceruteDeId) return
    const lista = agenti.data
    if (!lista) return
    implicitPus.current = true
    if (lista.some((a) => a.id === cerere.ceruteDeId)) setCatre(`agent:${cerere.ceruteDeId}`)
  }, [agenti.data, catre, cerere, retur])

  const corp = () => ({
    locSursa: Number(locSursa),
    /* Retur: marfa se INTOARCE. Serverul scade, nu aduna. Vezi `sensInvers`. */
    retur: retur === true,
    catreAgentId: catre.startsWith('agent:') ? Number(catre.slice(6)) : undefined,
    catreLocId: catre.startsWith('loc:') ? Number(catre.slice(4)) : undefined,
    observatii: observatii.trim() || undefined,
    campanieId,
    cerereId: cerere?.id,
    linii: Object.entries(selectie).map(([pid, cant]) => ({
      productId: Number(pid),
      cantitate: cant,
      cerereLinieId: linieCererePeProdus.get(Number(pid)),
    })),
  })

  const vezi = useMutation({
    mutationFn: async () =>
      (await api.post<Preview>('/marketing/sampling/transferuri/previzualizare', corp())).data,
    onSuccess: setPreview,
    onError: (e) => toast.error(mesaj(e)),
  })

  const trimite = useMutation({
    mutationFn: async () =>
      (await api.post<{ referinta: string }>('/marketing/sampling/transferuri', corp())).data,
    onSuccess: (r) => {
      toast.success(`Transfer trimis: ${r.referinta}`)
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
      void queryClient.invalidateQueries({ queryKey: ['campanie'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /* Înregistrarea: altă rută, care NU vorbește cu ExpertAccounts. Vezi `inregistrare` din props. */
  const inregistreaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ referinta: string }>('/marketing/sampling/transferuri/inregistreaza', {
          ...corp(),
          eaDocNo: eaDocNo.trim(),
          data: dataMutarii,
        })
      ).data,
    onSuccess: (r) => {
      toast.success(`Mutare înregistrată: ${r.referinta}. Nu s-a trimis nimic în EA.`)
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
      void queryClient.invalidateQueries({ queryKey: ['campanie'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const nrLinii = Object.keys(selectie).length

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        inregistrare
          ? 'Înregistrează o mutare făcută în ExpertAccounts'
          : retur
            ? 'Retur în depozit'
            : 'Transfer către gestiunea de sampling'
      }
      inchideLaClicInAfara={false}
    >
      <div className="space-y-3">
        {/*
          Avertismentul stă PRIMUL și e ferm. Riscul acestui ecran nu e tehnic, e de înțelegere:
          cineva îl folosește crezând că mută marfa, iar marfa nu se mișcă nicăieri.
        */}
        {inregistrare && (
          <div className="rounded-lg border border-b4l-amber bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
            <strong>Aici nu se mută nimic.</strong> Doar înregistrezi în CRM o mutare făcută deja în
            ExpertAccounts. Nu se trimite niciun document, iar stocul din ExpertAccounts rămâne
            neschimbat.
            <div className="mt-1">
              Dacă marfa <em>nu</em> s-a mutat încă, închide fereastra și folosește
              <strong> Transfer nou</strong> — acela chiar o mută.
            </div>
          </div>
        )}

        {inregistrare && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-b4l-text">Documentul din EA</span>
              <input
                value={eaDocNo}
                onChange={(e) => setEaDocNo(e.target.value)}
                placeholder="vt13"
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-b4l-text-muted">
                Obligatoriu — e dovada că mutarea s-a făcut. Același document nu se poate înregistra
                de două ori.
              </span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-b4l-text">Când s-a mutat</span>
              <input
                type="date"
                value={dataMutarii}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDataMutarii(e.target.value)}
                className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
              />
            </label>
          </div>
        )}

        {gestiuni.data && gestiuni.data.length > 0 ? (
          <label className="block">
            <span className="text-sm font-medium text-b4l-text">Din ce gestiune</span>
            <select
              value={locSursa}
              onChange={(e) => setLocSursa(e.target.value)}
              className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
            >
              {/* Cele fără stoc — servicii, discounturi, avansuri — n-au ce da. */}
              {gestiuni.data
                .filter((g) => !g.faraStoc)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.grup && g.grup !== g.nume ? `${g.grup} · ` : ''}
                    {g.nume} ({g.id})
                  </option>
                ))}
            </select>
            <span className="mt-1 block text-xs text-b4l-text-muted">
              Mostrele stau de obicei în „Mostre”, nu în „Depozit”. Materialele de marketing, în
              „Materiale mk”.
            </span>
          </label>
        ) : (
          <Field
            label="Gestiunea sursă (locid din EA)"
            type="number"
            value={locSursa}
            onChange={(e) => setLocSursa(e.target.value)}
            hint={
              gestiuni.isError
                ? 'Nu am putut citi gestiunile din EA — scrie numărul manual.'
                : 'Se încarcă lista de gestiuni…'
            }
          />
        )}

        <label className="block">
          <span className="text-sm font-medium text-b4l-text">Către</span>
          <select
            value={catre}
            onChange={(e) => setCatre(e.target.value)}
            className="mt-1 w-full rounded-lg border border-b4l-border px-3 py-2 text-sm"
          >
            <option value="">Gestiunea comună de sampling</option>
            {(agenti.data ?? []).length > 0 && (
              <optgroup label="Agenți">
                {(agenti.data ?? []).map((a) => (
                  <option key={a.id} value={`agent:${a.id}`}>
                    {a.name} · gestiunea {a.eaLocId}
                  </option>
                ))}
              </optgroup>
            )}
            {(gestiuni.data ?? []).length > 0 && (
              <optgroup label="Gestiuni">
                {(gestiuni.data ?? [])
                  .filter((g) => !g.faraStoc && String(g.id) !== locSursa)
                  .map((g) => (
                    <option key={g.id} value={`loc:${g.id}`}>
                      {g.grup && g.grup !== g.nume ? `${g.grup} · ` : ''}
                      {g.nume} ({g.id})
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
          <span className="mt-1 block text-xs text-b4l-text-muted">
            {agenti.data?.length
              ? 'Marfa intră în gestiunea agentului — stocul pe care îl poartă în mașină. Bonurile lui de consum scad tot de acolo.'
              : 'Niciun agent n-are gestiune proprie încă — se pune în Setări → Utilizatori, coloana „Gestiune EA”. Până atunci alegi direct o gestiune.'}
          </span>
        </label>

        <div>
          <p className="text-sm font-medium text-b4l-text">
            Ce se mută {nrLinii > 0 && `(${nrLinii} produse)`}
          </p>
          {!catalog.data?.produse.length ? (
            <p className="mt-2 text-sm text-b4l-amber-dark">
              Niciun produs bifat ca mostră (Sampling → Setări).
            </p>
          ) : (
            // Lista derulează în interiorul ei, nu împinge fereastra: cu tot catalogul afișat,
            // butoanele de jos ajungeau sub marginea ecranului (Alexandru, 22.09.2026).
            <div className="mt-2 max-h-80 space-y-2 overflow-auto rounded border border-b4l-border p-2">
              {catalog.data.produse.map((p) => {
                const ales = p.id in selectie
                return (
                  <div key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={ales}
                      onChange={(e) =>
                        setSelectie((s) => {
                          const copie = { ...s }
                          if (e.target.checked) copie[p.id] = s[p.id] ?? 10
                          else delete copie[p.id]
                          return copie
                        })
                      }
                      className="h-4 w-4"
                    />
                    <span className="flex-1">
                      {p.name}
                      <span className="ml-1 text-xs text-b4l-text-dim">{p.sku}</span>
                      {/*
                        Stocul din gestiunea sursă. Transferul din 21.08 a plecat cu un articol
                        care avea 0 aici — EA nu l-a refuzat, și-a inventat un lot. Cifra asta,
                        văzută înainte de bifă, e diferența dintre cele două.
                      */}
                      {p.stocEa === null ? (
                        <span className="ml-2 text-xs text-b4l-text-dim">stoc necunoscut</span>
                      ) : p.stocEa > 0 ? (
                        <span className="ml-2 text-xs text-b4l-green-dark">stoc {p.stocEa}</span>
                      ) : (
                        <span className="ml-2 text-xs text-b4l-red">fără stoc</span>
                      )}
                    </span>
                    <input
                      type="number"
                      min={1}
                      value={selectie[p.id] ?? 10}
                      disabled={!ales}
                      onChange={(e) =>
                        setSelectie((s) => ({
                          ...s,
                          [p.id]: Math.max(1, Number(e.target.value) || 1),
                        }))
                      }
                      className="w-20 rounded border border-b4l-border px-2 py-1 text-sm disabled:opacity-40"
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <Field
          label="Observații (opțional)"
          value={observatii}
          onChange={(e) => setObservatii(e.target.value)}
          placeholder="Ex.: pentru Sense4Fit, 20–21 august"
        />

        {preview && (
          <div className="space-y-2">
            <p className="text-xs text-b4l-text-muted">
              Prețul de pe fiecare linie e cerut de ExpertAccounts, fiindcă același API scrie și
              facturi de vânzare. La transfer nu decide nimic — mișcarea se valorizează la costul
              din stoc.
            </p>
            {preview.piedici.length > 0 && (
              <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
                <p className="font-medium">Nu pleacă acum:</p>
                <ul className="mt-1 list-inside list-disc">
                  {preview.piedici.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            <pre className="max-h-64 overflow-auto rounded-lg bg-b4l-bg p-3 text-xs">
              {JSON.stringify(preview.payload, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Anulează
        </Button>
        {/* Previzualizarea arată ce s-ar TRIMITE. La înregistrare nu se trimite nimic. */}
        {!inregistrare && (
          <Button
            variant="secondary"
            disabled={nrLinii === 0 || vezi.isPending}
            onClick={() => vezi.mutate()}
          >
            {vezi.isPending ? 'Se pregătește…' : 'Vezi ce se trimite'}
          </Button>
        )}
        {/*
          La înregistrare nu există „piedici": nu vorbim cu ExpertAccounts, deci n-are ce să ne
          oprească de acolo. Singura condiție în plus e numărul documentului.
        */}
        <Button
          disabled={
            nrLinii === 0 ||
            (inregistrare
              ? inregistreaza.isPending || eaDocNo.trim() === ''
              : trimite.isPending || (preview?.piedici.length ?? 0) > 0)
          }
          onClick={() => (inregistrare ? inregistreaza.mutate() : trimite.mutate())}
        >
          {inregistrare
            ? inregistreaza.isPending
              ? 'Se înregistrează…'
              : 'Înregistrează mutarea'
            : trimite.isPending
              ? 'Se trimite…'
              : retur
                ? 'Trimite returul în EA'
                : 'Trimite în EA'}
        </Button>
      </div>
    </Modal>
  )
}
